import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu,
  MenuItem, Chip, Badge, Popover, List as MuiList, ListItem as MuiListItem,
  Divider, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Button, Alert
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ViewListIcon from '@mui/icons-material/ViewList';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import HistoryIcon from '@mui/icons-material/History';
import ChatIcon from '@mui/icons-material/Chat';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import ChatPanel from './ChatPanel';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const drawerWidth = 220;

// Paleta para el portal del paciente
const TEMA_PACIENTE = {
  id: 'blanco',
  color_fondo:      '#eef1f5',
  color_superficie: '#f7f9fc',
  color_primario:   '#1d3557',
  color_acento:     '#1565c0',
  color_campo:      '#dce6f5',
};

function aplicarTema(tema) {
  const root = document.documentElement;
  root.style.setProperty('--c-fondo',      tema.color_fondo);
  root.style.setProperty('--c-superficie', tema.color_superficie);
  root.style.setProperty('--c-primario',   tema.color_primario);
  root.style.setProperty('--c-acento',     tema.color_acento);
  root.style.setProperty('--c-campo',      tema.color_campo || '#2a5aaa');
}

const TIPO_COLORES = {
  info:    { bg: '#0d47a1', text: '#bbdefb' },
  warning: { bg: '#e65100', text: '#fff3e0' },
  success: { bg: '#1b5e20', text: '#c8e6c9' },
  error:   { bg: '#b71c1c', text: '#ffcdd2' },
};

export default function Layout({ children, usuario, onLogout, onTemaChange, onPasswordCambiada }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const lastSeenIdRef = React.useRef(0);
  const [enRecepcion, setEnRecepcion] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [passDialogOpen, setPassDialogOpen] = useState(false);
  const [passForm, setPassForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [passError, setPassError] = useState('');
  const [passOk, setPassOk] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const cambioForzado = !!usuario.debe_cambiar_password;

  useEffect(() => {
    if (cambioForzado) {
      setPassForm({ actual: '', nueva: '', confirmar: '' });
      setPassError('');
      setPassOk(false);
      setPassDialogOpen(true);
    }
  }, [cambioForzado]);

  const tieneChat = ['admin', 'recepcionista', 'medico'].includes(usuario.rol);

  // El portal del paciente usa el mismo tema oscuro que el staff (TEMA_PACIENTE eliminado)

  // En mobile el drawer arranca cerrado
  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile]);

  useEffect(() => {
    axios.get(`${API}/api/avisos`)
      .then(r => setAvisos(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (usuario.rol !== 'medico') return;
    const poll = () => {
      axios.get(`${API}/api/turnos/en-recepcion`)
        .then(r => setEnRecepcion(r.data))
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 10000);
    return () => clearInterval(iv);
  }, [usuario.rol]);

  useEffect(() => {
    if (!tieneChat || chatOpen) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/chat/no-leidos`);
        setUnread(r.data.total || 0);
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [chatOpen, tieneChat]);

  const TODOS_ITEMS = [
    { text: 'Mis turnos',    icon: <DashboardIcon />,        path: '/panel-paciente',    roles: ['paciente'] },
    { text: 'Mis datos',     icon: <PersonIcon />,            path: '/mis-datos',         roles: ['paciente'] },
    { text: 'Calendario',    icon: <CalendarMonthIcon />,     path: '/calendario',        roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Turnos',        icon: <ViewListIcon />,          path: '/turnos',            roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Pacientes',     icon: <PeopleIcon />,            path: '/pacientes',         roles: ['admin', 'recepcionista'] },
    { text: 'Confirmaciones',icon: <MarkEmailReadIcon />,     path: '/confirmaciones',    roles: ['admin', 'recepcionista'] },
    { text: 'Caja',          icon: <PointOfSaleIcon />,       path: '/caja',              roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Búsqueda HC',   icon: <FindInPageIcon />,        path: '/busqueda-hc',       roles: ['admin', 'medico'] },
    { text: 'Profesionales', icon: <LocalHospitalIcon />,     path: '/medicos',           roles: ['admin', 'recepcionista'] },
    { text: 'Especialidades',icon: <MedicalServicesIcon />,   path: '/especialidades',    roles: ['admin', 'recepcionista'] },
    { text: 'Obras sociales',icon: <LocalHospitalIcon />,     path: '/obras-sociales',    roles: ['admin', 'recepcionista'] },
    { text: 'Usuarios',      icon: <ManageAccountsIcon />,    path: '/staff',             roles: ['admin'] },
    { text: 'Auditoría de Turnos',   icon: <HistoryIcon />,   path: '/auditoria-turnos',  roles: ['admin'] },
    { text: 'Auditoría de Médicos',  icon: <HistoryIcon />,   path: '/auditoria-medicos', roles: ['admin'] },
    { text: 'Configuración', icon: <SettingsIcon />,          path: '/configuracion',     roles: ['admin'] },
    { text: 'Manual',        icon: <MenuBookIcon />,          path: '/manual',            roles: ['admin', 'recepcionista', 'medico', 'paciente'] },
  ];

  // Para admin y recepcionista: secciones con encabezados. Para otros roles: lista plana.
  const SECCIONES_ADMIN = [
    { titulo: 'Operación',      paths: ['/calendario', '/turnos', '/pacientes', '/confirmaciones', '/caja', '/busqueda-hc'] },
    { titulo: 'Gestión médica', paths: ['/medicos', '/especialidades', '/obras-sociales'] },
    { titulo: 'Administración', paths: ['/staff', '/auditoria-turnos', '/auditoria-medicos', '/configuracion'] },
    { titulo: 'Ayuda',          paths: ['/manual'] },
  ];

  const menu = TODOS_ITEMS.filter(item => item.roles.includes(usuario.rol));

  function renderMenuItem(item) {
    const active = location.pathname === item.path;
    return (
      <ListItem button key={item.path} onClick={() => handleMenuClick(item.path)}
        title={item.text}
        sx={{
          bgcolor: active ? 'var(--c-primario)' : 'transparent',
          '&:hover': { bgcolor: 'var(--c-primario)' },
          borderRadius: 1, mx: 1, my: 0.3,
        }}
      >
        <ListItemIcon sx={{ color: active ? 'var(--c-acento)' : '#7a9bbf', minWidth: 40 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: 13,
            fontWeight: active ? 700 : 400,
            color: active ? 'var(--c-acento)' : '#b0bec5',
            letterSpacing: active ? 0.2 : 0,
          }}
        />
      </ListItem>
    );
  }

  function handleMenuClick(path) {
    navigate(path);
    if (isMobile) setOpen(false); // Cerrar drawer en mobile al navegar
  }

  function colorRol(rol) {
    const colores = { admin: 'error', medico: 'info', recepcionista: 'warning', paciente: 'success' };
    return colores[rol] || 'default';
  }

  function abrirCambiarPassword() {
    setAnchorEl(null);
    setPassForm({ actual: '', nueva: '', confirmar: '' });
    setPassError('');
    setPassOk(false);
    setPassDialogOpen(true);
  }

  async function guardarNuevaPassword() {
    setPassError('');
    if (!passForm.actual || !passForm.nueva || !passForm.confirmar) {
      setPassError('Completá los tres campos.');
      return;
    }
    if (passForm.nueva.length < 6) {
      setPassError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (passForm.nueva !== passForm.confirmar) {
      setPassError('La confirmación no coincide con la nueva contraseña.');
      return;
    }
    setPassLoading(true);
    try {
      await axios.put(`${API}/api/staff/me/password`, {
        password_actual: passForm.actual,
        password_nueva: passForm.nueva,
      });
      setPassOk(true);
      if (cambioForzado && onPasswordCambiada) onPasswordCambiada();
    } catch (e) {
      setPassError(e.response?.data?.error || 'No se pudo cambiar la contraseña.');
    } finally {
      setPassLoading(false);
    }
  }

  const topOffset = avisos.length > 0 ? '96px' : '64px';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--c-fondo)' }}>

      {/* ── AppBar ── */}
      <AppBar position="fixed" sx={{ zIndex: 1201, bgcolor: 'var(--c-superficie)' }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton color="inherit" onClick={() => setOpen(!open)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{
            color: '#e0e0e0', fontWeight: 700, flexGrow: 1,
            fontSize: { xs: 15, sm: 20 },
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            Sistema Médico
          </Typography>

          {/* Chip de rol — oculto en mobile muy pequeño */}
          <Chip
            label={usuario.rol}
            color={colorRol(usuario.rol)}
            size="small"
            sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }}
          />

          {/* Nombre del usuario — oculto en mobile */}
          <Typography sx={{ color: '#b0b0b0', mr: 1, fontSize: 14, display: { xs: 'none', md: 'block' } }}>
            {usuario.nombre}
          </Typography>

          {/* Notificaciones en recepción (solo médico) */}
          {usuario.rol === 'medico' && (
            <IconButton
              onClick={e => setNotifAnchor(e.currentTarget)}
              sx={{ mr: 0.5, color: enRecepcion.length > 0 ? '#ffb74d' : '#b0b0b0' }}
            >
              <Badge badgeContent={enRecepcion.length} color="warning" invisible={enRecepcion.length === 0}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: {
              bgcolor: 'var(--c-superficie)', color: '#e0e0e0',
              width: { xs: '90vw', sm: 320 },
              border: '1px solid var(--c-primario)',
            }}}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--c-primario)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#ffb74d' }}>
                Pacientes en recepción
              </Typography>
            </Box>
            {enRecepcion.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#666' }}>Sin pacientes esperando.</Typography>
              </Box>
            ) : (
              <MuiList dense disablePadding>
                {enRecepcion.map((t, i) => (
                  <React.Fragment key={t.id}>
                    {i > 0 && <Divider sx={{ borderColor: 'var(--c-primario)' }} />}
                    <MuiListItem sx={{ py: 1, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box
                        onClick={() => {
                          if (t.paciente_id) {
                            navigate(`/historial/${t.paciente_id}?nueva=1&turno=${t.id}`);
                            setNotifAnchor(null);
                          }
                        }}
                        sx={{ flex: 1, cursor: t.paciente_id ? 'pointer' : 'default', borderRadius: 1, px: 1, py: 0.5,
                          '&:hover': t.paciente_id ? { bgcolor: 'rgba(255,255,255,0.06)' } : {} }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.paciente_id ? 'var(--c-acento)' : '#e0e0e0' }}>
                          {t.paciente_nombre || '—'}
                          {t.paciente_id && <Typography component="span" sx={{ fontSize: 10, ml: 0.5, color: '#90a4ae' }}>→ HC</Typography>}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#90a4ae' }}>
                          {t.paciente_dni ? `DNI ${t.paciente_dni} · ` : ''}
                          {t.hora_inicio ? String(t.hora_inicio).substring(0, 5) : ''}
                          {t.medico_nombre ? ` · ${t.medico_nombre}` : ''}
                        </Typography>
                      </Box>
                      <IconButton size="small" title="Marcar como atendido"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/turnos/${t.id}`, { estado: 'atendido' });
                            setEnRecepcion(prev => prev.filter(x => x.id !== t.id));
                          } catch {}
                        }}
                        sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', flexShrink: 0, '&:hover': { bgcolor: 'rgba(102,187,106,0.3)' } }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, px: 0.5 }}>✓</Typography>
                      </IconButton>
                    </MuiListItem>
                  </React.Fragment>
                ))}
              </MuiList>
            )}
          </Popover>

          {/* Chat */}
          {tieneChat && (
            <IconButton
              onClick={() => { setChatOpen(o => !o); setUnread(0); }}
              sx={{ mr: 0.5, color: chatOpen ? 'var(--c-acento)' : '#b0b0b0',
                bgcolor: chatOpen ? 'rgba(144,202,249,0.1)' : 'transparent',
                '&:hover': { color: 'var(--c-acento)' } }}
            >
              <Badge badgeContent={unread > 9 ? '9+' : unread} color="error" invisible={unread === 0}
                sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, p: '0 3px' } }}>
                <ChatIcon />
              </Badge>
            </IconButton>
          )}

          <Avatar
            sx={{ bgcolor: 'var(--c-primario)', cursor: 'pointer', width: 32, height: 32, fontSize: 14 }}
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            {usuario.nombre?.charAt(0)}
          </Avatar>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
            <MenuItem disabled sx={{ fontSize: 13, opacity: 0.7 }}>{usuario.nombre}</MenuItem>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            {usuario.rol !== 'paciente' && (
              <MenuItem onClick={abrirCambiarPassword}>Cambiar contraseña</MenuItem>
            )}
            <MenuItem onClick={onLogout} sx={{ color: '#f48fb1' }}>Cerrar sesión</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Diálogo: cambiar contraseña ── */}
      <Dialog open={passDialogOpen}
        onClose={(cambioForzado && !passOk) ? undefined : () => setPassDialogOpen(false)}
        disableEscapeKeyDown={cambioForzado && !passOk}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle>Cambiar contraseña</DialogTitle>
        <DialogContent>
          {passOk ? (
            <Alert severity="success" sx={{ mt: 1 }}>Contraseña actualizada. La vas a usar desde el próximo inicio de sesión.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {cambioForzado && (
                <Alert severity="info">El administrador te pidió elegir una contraseña nueva antes de continuar.</Alert>
              )}
              {passError && <Alert severity="error">{passError}</Alert>}
              <TextField
                label="Contraseña actual" type="password" fullWidth
                value={passForm.actual}
                onChange={e => setPassForm(f => ({ ...f, actual: e.target.value }))}
                InputLabelProps={{ sx: { color: '#90a4ae' } }}
              />
              <TextField
                label="Nueva contraseña" type="password" fullWidth
                value={passForm.nueva}
                onChange={e => setPassForm(f => ({ ...f, nueva: e.target.value }))}
                InputLabelProps={{ sx: { color: '#90a4ae' } }}
              />
              <TextField
                label="Confirmar nueva contraseña" type="password" fullWidth
                value={passForm.confirmar}
                onChange={e => setPassForm(f => ({ ...f, confirmar: e.target.value }))}
                InputLabelProps={{ sx: { color: '#90a4ae' } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {passOk ? (
            <Button onClick={() => setPassDialogOpen(false)} variant="contained">Cerrar</Button>
          ) : (
            <>
              {!cambioForzado && <Button onClick={() => setPassDialogOpen(false)}>Cancelar</Button>}
              <Button onClick={guardarNuevaPassword} variant="contained" disabled={passLoading}>
                {passLoading ? 'Guardando...' : 'Guardar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Banner de avisos ── */}
      {avisos.length > 0 && (
        <Box sx={{
          position: 'fixed', top: { xs: 56, sm: 64 }, left: 0, right: 0, zIndex: 1200,
          bgcolor: TIPO_COLORES[avisos[0].tipo]?.bg || TIPO_COLORES.info.bg,
          color: TIPO_COLORES[avisos[0].tipo]?.text || TIPO_COLORES.info.text,
          overflow: 'hidden', height: 32, display: 'flex', alignItems: 'center',
        }}>
          <style>{`@keyframes marquee { 0% { transform: translateX(100vw); } 100% { transform: translateX(-100%); } }`}</style>
          <Box sx={{
            display: 'inline-block', whiteSpace: 'nowrap',
            animation: `marquee ${Math.max(28, avisos.map(a => a.texto.length).reduce((s, l) => s + l, 0) * 0.12)}s linear infinite`,
            fontSize: 12, fontWeight: 400, letterSpacing: 0.4, opacity: 0.92,
          }}>
            {avisos.map((a, i) => (
              <span key={a.id}>{a.texto}{i < avisos.length - 1 && <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>}</span>
            ))}
            <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>
            {avisos.map((a, i) => (
              <span key={`r-${a.id}`}>{a.texto}{i < avisos.length - 1 && <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>}</span>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Drawer ── */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            bgcolor: 'var(--c-superficie)',
            color: '#e0e0e0',
            mt: isMobile ? 0 : (avisos.length > 0 ? '96px' : '64px'),
            transition: 'margin-top 0.2s',
          }
        }}
      >
        {/* En mobile mostramos un header con botón cerrar */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid var(--c-primario)' }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--c-acento)', fontSize: 15 }}>Menú</Typography>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: '#b0b0b0' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <List sx={{ pt: 0.5 }}>
          {['admin', 'recepcionista'].includes(usuario.rol) ? (
            SECCIONES_ADMIN.map(seccion => {
              const itemsSeccion = seccion.paths.map(p => menu.find(m => m.path === p)).filter(Boolean);
              if (itemsSeccion.length === 0) return null;
              return (
                <Box key={seccion.titulo}>
                  <Typography sx={{
                    fontSize: 9, fontWeight: 700, color: '#3d5a6e',
                    textTransform: 'uppercase', letterSpacing: 1.2,
                    px: 2, pt: 1.5, pb: 0.5,
                  }}>
                    {seccion.titulo}
                  </Typography>
                  {itemsSeccion.map(item => renderMenuItem(item))}
                </Box>
              );
            })
          ) : (
            menu.map(item => renderMenuItem(item))
          )}
        </List>
      </Drawer>

      {/* ── Panel chat — fullscreen en mobile ── */}
      {tieneChat && chatOpen && (
        <Box sx={{
          position: 'fixed',
          top: isMobile ? 0 : topOffset,
          right: isMobile ? 0 : 24,
          left: isMobile ? 0 : 'auto',
          bottom: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 320,
          height: isMobile ? '100%' : `calc(100vh - ${topOffset === '96px' ? 120 : 88}px)`,
          bgcolor: 'var(--c-fondo)',
          border: '1px solid var(--c-primario)',
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zIndex: isMobile ? 1300 : 1100,
        }}>
          <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
        </Box>
      )}

      {/* ── Contenido principal ── */}
      <Box component="main" sx={{
        flexGrow: 1,
        p: { xs: 1.5, sm: 3 },
        mt: avisos.length > 0 ? { xs: '88px', sm: '96px' } : { xs: '56px', sm: '64px' },
        ml: !isMobile && !open ? `-${drawerWidth}px` : 0,
        transition: 'margin 0.3s',
        color: '#e0e0e0',
        minWidth: 0, // Evita overflow en flex
      }}>
        {children}
      </Box>
    </Box>
  );
}
